/**
 * Integrations Panel Component
 *
 * Manages form integrations with CRMs (Salesforce, HubSpot, Monday.com),
 * automation platforms (Zapier, Power Automate), and more.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useBuilderStore } from '../../stores/builderStore';
import type { IntegrationProvider, FieldMapping } from '../../types/form';

interface Integration {
  id: string;
  formId: string;
  provider: IntegrationProvider;
  name: string;
  enabled: boolean;
  status: 'active' | 'inactive' | 'error' | 'pending_auth';
  config: Record<string, unknown>;
  fieldMappings: FieldMapping[];
  triggerOn: string[];
  webhookUrl?: string;
  hasWebhookSecret: boolean;
  lastSyncAt?: string;
  lastError?: string;
  errorCount: number;
  successCount: number;
}

interface IntegrationTemplate {
  id: string;
  provider: IntegrationProvider;
  name: string;
  description: string;
  category: string;
  default_config: string;
  required_fields: string;
  icon_url?: string;
  is_featured: boolean;
  requires_oauth: boolean;
  requires_api_key: boolean;
}

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
}

interface IntegrationsPanelProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

const PROVIDER_ICONS: Record<IntegrationProvider, string> = {
  // CRM
  salesforce: '☁️',
  hubspot: '🟠',
  monday: '📊',
  pipedrive: '🔵',
  zoho_crm: '🔴',
  dynamics365: '🟣',
  freshsales: '🟢',
  // Automation
  zapier: '⚡',
  power_automate: '🔄',
  make: '🔗',
  n8n: '🔧',
  webhook: '🌐',
  // Spreadsheets & Databases
  google_sheets: '📗',
  airtable: '📋',
  notion: '📝',
  // Office 365 / Microsoft 365
  excel_online: '📊',
  onedrive: '☁️',
  sharepoint: '📁',
  outlook: '📬',
  onenote: '📓',
  microsoft_todo: '✅',
  planner: '📋',
  // Communication
  slack: '💬',
  teams: '👥',
  discord: '🎮',
  // Email Marketing
  mailchimp: '🐵',
  activecampaign: '📧',
  // Website Builders
  wix: '🌟',
};

const CATEGORY_LABELS: Record<string, string> = {
  crm: 'CRM & Sales',
  automation: 'Automation',
  spreadsheet: 'Spreadsheets & Databases',
  microsoft365: 'Microsoft 365',
  notification: 'Notifications',
  email: 'Email Marketing',
  website: 'Website Builders',
};

export default function IntegrationsPanel({ formId, isOpen, onClose }: IntegrationsPanelProps) {
  const { t } = useTranslation('admin');
  const { formSchema } = useBuilderStore();

  // Get form fields from builder store
  const fields: FormField[] = formSchema.fields.map(f => ({
    id: f.id,
    name: f.name,
    label: f.label,
    type: f.type,
  }));
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [templates, setTemplates] = useState<IntegrationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IntegrationTemplate | null>(null);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    loadIntegrations();
  }, [formId]);

  async function loadIntegrations() {
    try {
      setLoading(true);
      const response = await api.get(`/api/forms/${formId}/integrations`);
      if (!response.ok) throw new Error('Failed to load integrations');
      const data = await response.json() as { integrations?: Integration[]; templates?: IntegrationTemplate[] };
      setIntegrations(data.integrations || []);
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  async function createIntegration(template: IntegrationTemplate, config: Record<string, unknown>) {
    try {
      const response = await api.post(`/api/forms/${formId}/integrations`, {
        provider: template.provider,
        name: config.name || template.name,
        config,
        webhookUrl: config.webhookUrl,
        enabled: true,
      });

      if (!response.ok) throw new Error('Failed to create integration');

      const data = await response.json() as { integration: Integration };
      setIntegrations([data.integration, ...integrations]);
      setShowAddModal(false);
      setSelectedTemplate(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    }
  }

  async function updateIntegration(id: string, updates: Partial<Integration>) {
    try {
      const response = await api.put(`/api/forms/${formId}/integrations/${id}`, updates);
      if (!response.ok) throw new Error('Failed to update integration');

      const data = await response.json() as { integration: Integration };
      setIntegrations(integrations.map(i => i.id === id ? data.integration : i));
      setEditingIntegration(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update integration');
    }
  }

  async function deleteIntegration(id: string) {
    if (!confirm('Are you sure you want to delete this integration?')) return;

    try {
      const response = await api.delete(`/api/forms/${formId}/integrations/${id}`);
      if (!response.ok) throw new Error('Failed to delete integration');
      setIntegrations(integrations.filter(i => i.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  }

  async function testIntegration(id: string) {
    try {
      setTestingId(id);
      setTestResult(null);

      const response = await api.post(`/api/forms/${formId}/integrations/${id}/test`, {});
      const data = await response.json() as { success: boolean; durationMs?: number; error?: string };

      setTestResult({
        success: data.success,
        message: data.success
          ? `Test successful! Response time: ${data.durationMs}ms`
          : data.error || 'Test failed',
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Test failed',
      });
    } finally {
      setTestingId(null);
    }
  }

  async function toggleIntegration(id: string, enabled: boolean) {
    await updateIntegration(id, { enabled });
  }

  // Group templates by category
  const templatesByCategory = templates.reduce((acc, template) => {
    const category = template.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(template);
    return acc;
  }, {} as Record<string, IntegrationTemplate[]>);

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={onClose} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('integrations.title', 'Integrations')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('integrations.description', 'Connect your form to CRMs, automation tools, and more')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('integrations.addIntegration', 'Add Integration')}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
        </div>
      )}

      {/* Active Integrations */}
      {integrations.length > 0 ? (
        <div className="space-y-4">
          {integrations.map(integration => (
            <div
              key={integration.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{PROVIDER_ICONS[integration.provider] || '🔌'}</span>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">
                      {integration.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {integration.provider.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Status Badge */}
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    integration.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                    integration.status === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                    integration.status === 'pending_auth' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {integration.status}
                  </span>

                  {/* Toggle */}
                  <button
                    onClick={() => toggleIntegration(integration.id, !integration.enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      integration.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        integration.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-3 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {integration.successCount} successful
                </span>
                {integration.errorCount > 0 && (
                  <span className="flex items-center gap-1 text-red-500">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {integration.errorCount} errors
                  </span>
                )}
                {integration.lastSyncAt && (
                  <span>Last sync: {new Date(integration.lastSyncAt).toLocaleString()}</span>
                )}
              </div>

              {/* Error Message */}
              {integration.lastError && (
                <div className="mt-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  {integration.lastError}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => testIntegration(integration.id)}
                  disabled={testingId === integration.id}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  {testingId === integration.id ? 'Testing...' : 'Test'}
                </button>
                <button
                  onClick={() => setEditingIntegration(integration)}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Configure
                </button>
                <button
                  onClick={() => deleteIntegration(integration.id)}
                  className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  Delete
                </button>
              </div>

              {/* Test Result */}
              {testResult && integration.id === editingIntegration?.id && (
                <div className={`mt-2 text-sm p-2 rounded ${
                  testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {testResult.message}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <div className="text-4xl mb-4">🔌</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('integrations.noIntegrations', 'No integrations yet')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('integrations.noIntegrationsDescription', 'Connect your form to external services to automate your workflow')}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('integrations.addFirst', 'Add Your First Integration')}
          </button>
        </div>
      )}

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedTemplate ? `Configure ${selectedTemplate.name}` : 'Add Integration'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedTemplate(null);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedTemplate ? (
                <IntegrationConfigForm
                  template={selectedTemplate}
                  fields={fields}
                  onSave={(config) => createIntegration(selectedTemplate, config)}
                  onBack={() => setSelectedTemplate(null)}
                />
              ) : (
                <div className="space-y-6">
                  {Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
                    <div key={category}>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                        {CATEGORY_LABELS[category] || category}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {categoryTemplates.map(template => (
                          <button
                            key={template.id}
                            onClick={() => setSelectedTemplate(template)}
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{PROVIDER_ICONS[template.provider] || '🔌'}</span>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {template.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                  {template.description}
                                </p>
                              </div>
                            </div>
                            {template.is_featured && (
                              <span className="mt-2 inline-block px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                                Popular
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

          {/* Edit Integration Modal */}
          {editingIntegration && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Configure {editingIntegration.name}
                  </h3>
                  <button
                    onClick={() => setEditingIntegration(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  <EditIntegrationForm
                    integration={editingIntegration}
                    fields={fields}
                    onSave={(updates) => updateIntegration(editingIntegration.id, updates)}
                    onTest={() => testIntegration(editingIntegration.id)}
                    testResult={testResult}
                    testing={testingId === editingIntegration.id}
                  />
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Configuration form for new integrations
function IntegrationConfigForm({
  template,
  fields,
  onSave,
  onBack,
}: {
  template: IntegrationTemplate;
  fields: FormField[];
  onSave: (config: Record<string, unknown>) => void;
  onBack: () => void;
}) {
  const defaultConfig = JSON.parse(template.default_config || '{}');
  // Parse required fields for future validation (provider requirements)
  JSON.parse(template.required_fields || '[]');
  const [config, setConfig] = useState<Record<string, unknown>>({
    name: template.name,
    ...defaultConfig,
  });
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);

  const isWebhookProvider = ['webhook', 'zapier', 'power_automate', 'make', 'n8n'].includes(template.provider);

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to integrations
      </button>

      <div className="flex items-center gap-3">
        <span className="text-3xl">{PROVIDER_ICONS[template.provider] || '🔌'}</span>
        <div>
          <h4 className="font-semibold text-lg">{template.name}</h4>
          <p className="text-sm text-gray-500">{template.description}</p>
        </div>
      </div>

      {/* Integration Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Integration Name
        </label>
        <input
          type="text"
          value={config.name as string || ''}
          onChange={(e) => setConfig({ ...config, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
          placeholder="My Integration"
        />
      </div>

      {/* Webhook URL for webhook-based integrations */}
      {isWebhookProvider && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Webhook URL *
          </label>
          <input
            type="url"
            value={config.webhookUrl as string || ''}
            onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
            placeholder="https://hooks.zapier.com/..."
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            {template.provider === 'zapier' && 'Get this URL from your Zapier Zap'}
            {template.provider === 'power_automate' && 'Get this URL from Power Automate Flow trigger'}
            {template.provider === 'make' && 'Get this URL from your Make scenario webhook'}
            {template.provider === 'webhook' && 'Enter any URL that accepts POST requests'}
          </p>
        </div>
      )}

      {/* Provider-specific fields */}
      {template.provider === 'slack' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Slack Webhook URL *
          </label>
          <input
            type="url"
            value={config.webhookUrl as string || ''}
            onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
            placeholder="https://hooks.slack.com/services/..."
          />
        </div>
      )}

      {template.provider === 'teams' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Teams Webhook URL *
          </label>
          <input
            type="url"
            value={config.webhookUrl as string || ''}
            onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
            placeholder="https://outlook.office.com/webhook/..."
          />
        </div>
      )}

      {template.provider === 'discord' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Discord Webhook URL *
          </label>
          <input
            type="url"
            value={config.webhookUrl as string || ''}
            onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
            placeholder="https://discord.com/api/webhooks/..."
          />
        </div>
      )}

      {template.provider === 'airtable' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Airtable API Key *
            </label>
            <input
              type="password"
              value={config.apiKey as string || ''}
              onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
              placeholder="pat..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Base ID *
              </label>
              <input
                type="text"
                value={config.baseId as string || ''}
                onChange={(e) => setConfig({ ...config, baseId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                placeholder="appXXXXXXXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Table ID *
              </label>
              <input
                type="text"
                value={config.tableId as string || ''}
                onChange={(e) => setConfig({ ...config, tableId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                placeholder="tblXXXXXXXXXXXXXX"
              />
            </div>
          </div>
        </>
      )}

      {/* OAuth-based integrations */}
      {template.requires_oauth && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                Authorization Required
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                After creating this integration, you'll need to connect your {template.name} account.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Field Mappings */}
      <div>
        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Field Mappings (Optional)
        </h5>
        <p className="text-xs text-gray-500 mb-3">
          Map your form fields to the external service. If not specified, all fields will be sent with their original names.
        </p>

        {fieldMappings.map((mapping, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <select
              value={mapping.formFieldId}
              onChange={(e) => {
                const field = fields.find(f => f.id === e.target.value);
                const newMappings = [...fieldMappings];
                newMappings[index] = {
                  ...mapping,
                  formFieldId: e.target.value,
                  formFieldName: field?.name || '',
                };
                setFieldMappings(newMappings);
              }}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
            >
              <option value="">Select form field</option>
              {fields.map(field => (
                <option key={field.id} value={field.id}>{field.label}</option>
              ))}
            </select>
            <span className="text-gray-400">→</span>
            <input
              type="text"
              value={mapping.targetField}
              onChange={(e) => {
                const newMappings = [...fieldMappings];
                newMappings[index] = { ...mapping, targetField: e.target.value };
                setFieldMappings(newMappings);
              }}
              placeholder="Target field name"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700"
            />
            <button
              onClick={() => setFieldMappings(fieldMappings.filter((_, i) => i !== index))}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}

        <button
          onClick={() => setFieldMappings([...fieldMappings, { formFieldId: '', formFieldName: '', targetField: '' }])}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Field Mapping
        </button>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave({ ...config, fieldMappings })}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create Integration
        </button>
      </div>
    </div>
  );
}

// Edit form for existing integrations
function EditIntegrationForm({
  integration,
  fields,
  onSave,
  onTest,
  testResult,
  testing,
}: {
  integration: Integration;
  fields: FormField[];
  onSave: (updates: Partial<Integration>) => void;
  onTest: () => void;
  testResult: { success: boolean; message: string } | null;
  testing: boolean;
}) {
  const [config, setConfig] = useState(integration.config);
  const [fieldMappings, setFieldMappings] = useState(integration.fieldMappings);
  const [triggerOn, setTriggerOn] = useState(integration.triggerOn);

  const isWebhookProvider = ['webhook', 'zapier', 'power_automate', 'make', 'n8n', 'slack', 'teams', 'discord'].includes(integration.provider);

  return (
    <div className="space-y-6">
      {/* Webhook URL */}
      {isWebhookProvider && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Webhook URL
          </label>
          <input
            type="url"
            value={config.webhookUrl as string || integration.webhookUrl || ''}
            onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
          />
        </div>
      )}

      {/* Trigger Events */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Trigger On
        </label>
        <div className="flex items-center gap-4">
          {['submission', 'update', 'delete'].map(event => (
            <label key={event} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={triggerOn.includes(event)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTriggerOn([...triggerOn, event]);
                  } else {
                    setTriggerOn(triggerOn.filter(t => t !== event));
                  }
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm capitalize">{event}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Field Mappings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Field Mappings
        </label>

        {fieldMappings.map((mapping, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <select
              value={mapping.formFieldId}
              onChange={(e) => {
                const field = fields.find(f => f.id === e.target.value);
                const newMappings = [...fieldMappings];
                newMappings[index] = {
                  ...mapping,
                  formFieldId: e.target.value,
                  formFieldName: field?.name || '',
                };
                setFieldMappings(newMappings);
              }}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 text-sm"
            >
              <option value="">Select form field</option>
              {fields.map(field => (
                <option key={field.id} value={field.id}>{field.label}</option>
              ))}
            </select>
            <span className="text-gray-400">→</span>
            <input
              type="text"
              value={mapping.targetField}
              onChange={(e) => {
                const newMappings = [...fieldMappings];
                newMappings[index] = { ...mapping, targetField: e.target.value };
                setFieldMappings(newMappings);
              }}
              placeholder="Target field"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 text-sm"
            />
            <button
              onClick={() => setFieldMappings(fieldMappings.filter((_, i) => i !== index))}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}

        <button
          onClick={() => setFieldMappings([...fieldMappings, { formFieldId: '', formFieldName: '', targetField: '' }])}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Mapping
        </button>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`p-3 rounded-lg text-sm ${
          testResult.success
            ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
        }`}>
          {testResult.message}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onTest}
          disabled={testing}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
        <button
          onClick={() => onSave({
            config,
            fieldMappings,
            triggerOn,
            webhookUrl: config.webhookUrl as string,
          })}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
